import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import {
  TrendingUp, TrendingDown, DollarSign, Users, Building2, Ticket,
  BarChart3, Target, RefreshCw, AlertCircle, Layers, Cpu, ArrowUpRight, ArrowDownRight,
  GitCommit, Zap, Code2, Bug
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ComposedChart, Area
} from 'recharts';
import { authFetch } from '@/utils/auth-fetch';

interface KPISummary {
  currentPlanMonth: number;
  timestamp: string;
  sources: Record<string, string>;
  financial: {
    totalInvoiced: number;
    totalCollected: number;
    totalExpenses: number;
    monthRevenue: number;
    monthExpenses: number;
    monthPayments: number;
    activeClients: number;
    unpaidInvoices: Array<{ id: string; number: string; client: string; amount: number; balance: number; due_date: string }>;
    expensesByVendor: Array<{ vendor: string; total: number }>;
    expenseHistory: Array<{ month: string; total: number }>;
    revenueHistory: Array<{ month: string; total: number }>;
  };
  pipeline: {
    totalContacts: number;
    newContactsLast30d: number;
    recentLeads: Array<{ name: string; email: string; company: string; createdAt: string }>;
  };
  platform: {
    totalSites: number;
    totalProducts: number;
    totalAEOGenerations: number;
    aeoByStatus: Record<string, number>;
    totalEmbeddings: number;
  };
  support: {
    activeTickets: number;
    pendingTickets: number;
    totalOpen: number;
  };
  budget: {
    month: number;
    Total_MRR: number;
    ARR: number;
    Num_Customers: number;
    Total_Costs: number;
    EBITDA: number;
    Cash: number;
    'Gross Margin %': number;
    'Runway (months)': number;
    Num_employees: number;
    Team_Cost: number;
    Cloud_APIs: number;
    Marketing: number;
    ARPU: number;
  } | null;
  budgetTimeline: Array<{
    month: number;
    Total_MRR: number;
    Total_Costs: number;
    EBITDA: number;
    Cash: number;
    Num_Customers: number;
  }>;
  pricing: Record<string, { price: number; included_regenerations: number }>;
  annualSummary: Record<string, { revenue: number; costs: number; ebitda: number }>;
  valuation: {
    enterprise_value_arr_method: number;
    enterprise_value_gordon: number;
    average_enterprise_value: number;
    initial_cash: number;
  };
}

function formatEUR(value: number): string {
  return new Intl.NumberFormat('en-EU', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
}

function formatK(value: number): string {
  if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toFixed(0);
}

function MetricCard({ title, actual, budget, icon: Icon, format = 'currency', inverse = false }: {
  title: string; actual: number; budget: number | null; icon: React.ElementType; format?: string; inverse?: boolean;
}) {
  const fmt = format === 'currency' ? formatEUR : format === 'number' ? (v: number) => formatK(v) : (v: number) => `${v.toFixed(1)}%`;
  const variance = budget && budget !== 0 ? ((actual - budget) / Math.abs(budget)) * 100 : null;
  const isPositive = variance !== null ? (inverse ? variance < 0 : variance > 0) : null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{fmt(actual)}</div>
        {budget !== null && budget !== undefined && (
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-muted-foreground">Budget: {fmt(budget)}</span>
            {variance !== null && (
              <Badge variant={isPositive ? 'default' : 'destructive'} className="text-xs">
                {isPositive ? <ArrowUpRight className="h-3 w-3 mr-0.5" /> : <ArrowDownRight className="h-3 w-3 mr-0.5" />}
                {Math.abs(variance).toFixed(0)}%
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SourceStatus({ sources }: { sources: Record<string, string> }) {
  return (
    <div className="flex gap-2">
      {Object.entries(sources).map(([name, status]) => (
        <Badge key={name} variant={status === 'ok' ? 'outline' : 'destructive'} className="text-xs">
          {name}: {status === 'ok' ? 'OK' : 'Error'}
        </Badge>
      ))}
    </div>
  );
}

interface TeamMetricRow {
  date: string;
  commits: number;
  fixes: number;
  features: number;
  deploys: number;
  locNet: number;
  fixRate: number;
  demand: number;
  offering: number;
  answer: number;
  conversation: number;
  impact: number;
  sessions: number;
  tokensTotal: number;
  costUsd: number;
}

interface TeamMetrics {
  days: number;
  rows: TeamMetricRow[];
  totals: {
    commits: number;
    fixes: number;
    feats: number;
    deploys: number;
    locNet: number;
    cost: number;
    tokens: number;
    sessions: number;
    fixRate: number;
  };
}

export default function BusinessKPIDashboard() {
  const [data, setData] = useState<KPISummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teamMetrics, setTeamMetrics] = useState<TeamMetrics | null>(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const teamFetchedRef = useRef(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch('/api/business-kpi/summary');
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchTeamMetrics = useCallback(async () => {
    if (teamFetchedRef.current) return;
    teamFetchedRef.current = true;
    setTeamLoading(true);
    try {
      const res = await authFetch('/api/business-kpi/team-metrics?days=30');
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      setTeamMetrics(await res.json());
    } catch {
      // non-critical: table may not exist yet (migration pending)
    } finally {
      setTeamLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex items-center gap-3 p-6">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <div>
              <p className="font-medium">Failed to load Business KPIs</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} className="ml-auto">
              <RefreshCw className="h-4 w-4 mr-2" /> Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const { financial, pipeline, platform, support, budget, budgetTimeline, annualSummary, valuation } = data;

  // Build combined chart data for revenue vs expenses (actual history + budget)
  const revenueVsExpenses = (financial.revenueHistory || []).map((r, i) => ({
    month: r.month,
    revenue: r.total,
    expenses: financial.expenseHistory?.[i]?.total || 0
  }));

  // Budget timeline for projection chart (first 24 months)
  const projectionData = (budgetTimeline || []).slice(0, 24).map(m => {
    const date = new Date(2026, m.month - 1, 1);
    const label = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    return {
      month: label,
      planMonth: m.month,
      budgetMRR: m.Total_MRR,
      budgetCosts: m.Total_Costs,
      budgetEBITDA: m.EBITDA,
      budgetCash: m.Cash,
      budgetCustomers: m.Num_Customers,
      isCurrent: m.month === data.currentPlanMonth
    };
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Business KPIs</h1>
          <p className="text-muted-foreground">
            PageMind SL — Plan month {data.currentPlanMonth}/36
            {data.timestamp && <span className="ml-2 text-xs">Updated {new Date(data.timestamp).toLocaleTimeString()}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SourceStatus sources={data.sources} />
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" onValueChange={(v) => { if (v === 'team') fetchTeamMetrics(); }}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="financial">Financial</TabsTrigger>
          <TabsTrigger value="platform">Platform</TabsTrigger>
          <TabsTrigger value="projections">Projections</TabsTrigger>
          <TabsTrigger value="team">AI Team</TabsTrigger>
        </TabsList>

        {/* ── OVERVIEW TAB ── */}
        <TabsContent value="overview" className="space-y-6">
          {/* Top KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard title="Total Revenue" actual={financial.totalInvoiced} budget={budget?.Total_MRR ? budget.Total_MRR * data.currentPlanMonth : null} icon={DollarSign} />
            <MetricCard title="Monthly Expenses" actual={financial.monthExpenses} budget={budget?.Total_Costs || null} icon={TrendingDown} format="currency" inverse />
            <MetricCard title="Active Clients" actual={financial.activeClients} budget={budget?.Num_Customers || null} icon={Building2} format="number" />
            <MetricCard title="Pipeline (Contacts)" actual={pipeline.totalContacts} budget={null} icon={Users} format="number" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard title="Unpaid Invoices" actual={financial.unpaidInvoices.reduce((s, i) => s + i.balance, 0)} budget={null} icon={AlertCircle} />
            <MetricCard title="Open Tickets" actual={support.totalOpen} budget={null} icon={Ticket} format="number" />
            <MetricCard title="AEO Published" actual={platform.aeoByStatus?.published || 0} budget={null} icon={Layers} format="number" />
            <MetricCard title="Products Managed" actual={platform.totalProducts} budget={null} icon={Cpu} format="number" />
          </div>

          {/* Revenue vs Expenses Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Revenue vs Expenses (Last 6 Months)</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={revenueVsExpenses}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value: number) => formatEUR(value)} />
                    <Legend />
                    <Bar dataKey="revenue" fill="#22c55e" name="Revenue" />
                    <Bar dataKey="expenses" fill="#ef4444" name="Expenses" />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Expenses by Vendor (This Month)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(financial.expensesByVendor || []).slice(0, 10).map((e) => (
                    <div key={e.vendor} className="flex items-center justify-between">
                      <span className="text-sm truncate flex-1">{e.vendor}</span>
                      <span className="text-sm font-medium ml-2">{formatEUR(e.total)}</span>
                    </div>
                  ))}
                  {(!financial.expensesByVendor || financial.expensesByVendor.length === 0) && (
                    <p className="text-sm text-muted-foreground">No expenses this month</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Leads */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Leads (Last 30 Days)</CardTitle>
              <CardDescription>{pipeline.newContactsLast30d} new contacts</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(pipeline.recentLeads || []).slice(0, 10).map((lead, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b pb-2">
                    <div>
                      <span className="font-medium">{lead.name}</span>
                      {lead.company && <span className="text-muted-foreground ml-2">({lead.company})</span>}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {lead.email} — {new Date(lead.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
                {(!pipeline.recentLeads || pipeline.recentLeads.length === 0) && (
                  <p className="text-sm text-muted-foreground">No recent leads</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── FINANCIAL TAB ── */}
        <TabsContent value="financial" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MetricCard title="Total Invoiced" actual={financial.totalInvoiced} budget={null} icon={DollarSign} />
            <MetricCard title="Total Collected" actual={financial.totalCollected} budget={null} icon={TrendingUp} />
            <MetricCard title="Total Expenses" actual={financial.totalExpenses} budget={null} icon={TrendingDown} inverse />
          </div>

          {/* Annual Summary from Budget */}
          {annualSummary && (
            <Card>
              <CardHeader>
                <CardTitle>3-Year Plan Summary</CardTitle>
                <CardDescription>From financial model v12.1</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(annualSummary).map(([year, data]) => (
                    <div key={year} className="space-y-2">
                      <h4 className="font-semibold capitalize">{year.replace('year', 'Year ')}</h4>
                      <div className="text-sm space-y-1">
                        <div className="flex justify-between"><span>Revenue</span><span className="font-medium">{formatEUR(data.revenue)}</span></div>
                        <div className="flex justify-between"><span>Costs</span><span className="font-medium">{formatEUR(data.costs)}</span></div>
                        <div className={`flex justify-between ${data.ebitda >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          <span>EBITDA</span><span className="font-medium">{formatEUR(data.ebitda)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Unpaid Invoices */}
          {financial.unpaidInvoices.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-red-600">Unpaid Invoices</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {financial.unpaidInvoices.map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between text-sm border-b pb-2">
                      <div>
                        <span className="font-medium">#{inv.number}</span>
                        <span className="text-muted-foreground ml-2">{inv.client}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-medium text-red-600">{formatEUR(inv.balance)}</span>
                        <span className="text-muted-foreground text-xs ml-2">of {formatEUR(inv.amount)}</span>
                        {inv.due_date && <span className="text-muted-foreground text-xs ml-2">due {inv.due_date}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Valuation */}
          {valuation && (
            <Card>
              <CardHeader>
                <CardTitle>DCF Valuation (Plan)</CardTitle>
                <CardDescription>Based on 36-month projection + 2-year extrapolation</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">ARR Multiple (10x)</p>
                    <p className="text-2xl font-bold">{formatEUR(valuation.enterprise_value_arr_method)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Gordon Growth</p>
                    <p className="text-2xl font-bold">{formatEUR(valuation.enterprise_value_gordon)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Average EV</p>
                    <p className="text-2xl font-bold text-blue-600">{formatEUR(valuation.average_enterprise_value)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── PLATFORM TAB ── */}
        <TabsContent value="platform" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard title="Active Sites" actual={platform.totalSites} budget={null} icon={Building2} format="number" />
            <MetricCard title="Products" actual={platform.totalProducts} budget={null} icon={Cpu} format="number" />
            <MetricCard title="AEO Generations" actual={platform.totalAEOGenerations} budget={null} icon={Layers} format="number" />
            <MetricCard title="Embeddings" actual={platform.totalEmbeddings} budget={null} icon={BarChart3} format="number" />
          </div>

          {/* AEO Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>AEO Content Pipeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(platform.aeoByStatus || {}).sort(([, a], [, b]) => b - a).map(([status, count]) => {
                  const total = platform.totalAEOGenerations || 1;
                  const pct = (count / total) * 100;
                  const colorMap: Record<string, string> = {
                    published: 'bg-green-500', draft: 'bg-yellow-500', archived: 'bg-gray-400', rejected: 'bg-red-500'
                  };
                  return (
                    <div key={status} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="capitalize">{status}</span>
                        <span>{formatK(count)} ({pct.toFixed(1)}%)</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${colorMap[status] || 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Support */}
          <Card>
            <CardHeader>
              <CardTitle>Support Load</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-3xl font-bold">{support.activeTickets}</p>
                  <p className="text-sm text-muted-foreground">Active</p>
                </div>
                <div>
                  <p className="text-3xl font-bold">{support.pendingTickets}</p>
                  <p className="text-sm text-muted-foreground">Pending</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-orange-600">{support.totalOpen}</p>
                  <p className="text-sm text-muted-foreground">Total Open</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── PROJECTIONS TAB ── */}
        <TabsContent value="projections" className="space-y-6">
          {/* MRR Projection */}
          <Card>
            <CardHeader>
              <CardTitle>MRR Projection (24 Months)</CardTitle>
              <CardDescription>Budget plan v12.1 — current month marked</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={projectionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={2} />
                  <YAxis />
                  <Tooltip formatter={(value: number) => formatEUR(value)} />
                  <Legend />
                  <Area type="monotone" dataKey="budgetMRR" fill="#dbeafe" stroke="#3b82f6" name="Budget MRR" />
                  <Line type="monotone" dataKey="budgetCosts" stroke="#ef4444" strokeDasharray="5 5" name="Budget Costs" />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Cash & EBITDA */}
          <Card>
            <CardHeader>
              <CardTitle>Cash Position & EBITDA (24 Months)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={projectionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={2} />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip formatter={(value: number) => formatEUR(value)} />
                  <Legend />
                  <Area yAxisId="left" type="monotone" dataKey="budgetCash" fill="#dcfce7" stroke="#22c55e" name="Cash" />
                  <Line yAxisId="right" type="monotone" dataKey="budgetEBITDA" stroke="#8b5cf6" name="EBITDA" />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Customer Growth */}
          <Card>
            <CardHeader>
              <CardTitle>Customer Growth Projection</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={projectionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={2} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="budgetCustomers" stroke="#f59e0b" name="Budget Customers" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Budget vs Actual Table */}
          {budget && (
            <Card>
              <CardHeader>
                <CardTitle>Month {data.currentPlanMonth}: Budget vs Actual</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { label: 'MRR', actual: financial.monthRevenue, budget: budget.Total_MRR },
                    { label: 'Total Costs', actual: financial.monthExpenses, budget: budget.Total_Costs, inverse: true },
                    { label: 'Team Cost', actual: 0, budget: budget.Team_Cost, note: 'Manual entry needed' },
                    { label: 'Cloud/APIs', actual: 0, budget: budget.Cloud_APIs, note: 'From Invoice Ninja expenses' },
                    { label: 'Marketing', actual: 0, budget: budget.Marketing },
                    { label: 'Customers', actual: financial.activeClients, budget: budget.Num_Customers, fmt: 'num' },
                    { label: 'Employees', actual: 0, budget: budget.Num_employees, fmt: 'num', note: 'Manual entry needed' },
                  ].map((row) => {
                    const variance = row.budget ? ((row.actual - row.budget) / Math.abs(row.budget)) * 100 : null;
                    return (
                      <div key={row.label} className="flex items-center justify-between text-sm border-b pb-2">
                        <span className="w-32">{row.label}</span>
                        <span className="w-24 text-right font-medium">
                          {row.fmt === 'num' ? row.actual : formatEUR(row.actual)}
                        </span>
                        <span className="w-24 text-right text-muted-foreground">
                          {row.fmt === 'num' ? row.budget : formatEUR(row.budget)}
                        </span>
                        <span className="w-20 text-right">
                          {row.note ? (
                            <span className="text-xs text-muted-foreground">{row.note}</span>
                          ) : variance !== null ? (
                            <Badge variant={Math.abs(variance) < 20 ? 'outline' : 'destructive'} className="text-xs">
                              {variance > 0 ? '+' : ''}{variance.toFixed(0)}%
                            </Badge>
                          ) : null}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── AI TEAM TAB ── */}
        <TabsContent value="team" className="space-y-6">
          {teamLoading && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-28" />)}
            </div>
          )}

          {!teamLoading && !teamMetrics && (
            <Card>
              <CardContent className="flex items-center gap-3 p-6">
                <AlertCircle className="h-5 w-5 text-yellow-500" />
                <div>
                  <p className="font-medium">No team metrics yet</p>
                  <p className="text-sm text-muted-foreground">
                    Run <code className="bg-muted px-1 rounded">bash scripts/metrics/collect-daily-metrics.sh</code> once to populate, or wait for tonight's cron (23:55).
                    The team_metrics migration must be deployed first.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {!teamLoading && teamMetrics && teamMetrics.rows.length === 0 && (
            <Card>
              <CardContent className="p-6 text-center text-muted-foreground">
                No data in the last {teamMetrics.days} days. Cron runs daily at 23:55.
              </CardContent>
            </Card>
          )}

          {!teamLoading && teamMetrics && teamMetrics.rows.length > 0 && (() => {
            const { totals, rows } = teamMetrics;
            const fixRateColor = totals.fixRate > 50 ? 'text-red-600' : totals.fixRate > 30 ? 'text-yellow-600' : 'text-green-600';
            const studioData = rows.map(r => ({
              date: r.date.toString().slice(5, 10),
              Demand: r.demand,
              Offering: r.offering,
              Answer: r.answer,
              Conversation: r.conversation,
              Impact: r.impact,
            }));
            const trendData = rows.map(r => ({
              date: r.date.toString().slice(5, 10),
              Features: r.features,
              'Bug Fixes': r.fixes,
              Deploys: r.deploys,
              'LoC Net': r.locNet,
            }));
            const costData = rows.map(r => ({
              date: r.date.toString().slice(5, 10),
              'Cost (USD)': r.costUsd,
              'Tokens (K)': Math.round(r.tokensTotal / 1000),
            }));
            return (
              <>
                {/* Stat tiles */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Features Shipped</CardTitle>
                      <Zap className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{totals.feats}</div>
                      <p className="text-xs text-muted-foreground">last 30 days</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Bug Fixes</CardTitle>
                      <Bug className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{totals.fixes}</div>
                      <p className="text-xs text-muted-foreground">last 30 days</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Fix Rate</CardTitle>
                      <BarChart3 className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className={`text-2xl font-bold ${fixRateColor}`}>{totals.fixRate.toFixed(0)}%</div>
                      <p className="text-xs text-muted-foreground">target &lt;30% — fixes / total commits</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Deploys</CardTitle>
                      <GitCommit className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{totals.deploys}</div>
                      <p className="text-xs text-muted-foreground">last 30 days</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Net Lines of Code</CardTitle>
                      <Code2 className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">+{formatK(totals.locNet)}</div>
                      <p className="text-xs text-muted-foreground">last 30 days</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Claude Sessions</CardTitle>
                      <Cpu className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{totals.sessions}</div>
                      <p className="text-xs text-muted-foreground">last 30 days</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">AI Cost (USD)</CardTitle>
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">${totals.cost.toFixed(0)}</div>
                      <p className="text-xs text-muted-foreground">last 30 days (est.)</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Tokens Processed</CardTitle>
                      <Layers className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{formatK(totals.tokens)}</div>
                      <p className="text-xs text-muted-foreground">last 30 days</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Features vs Bug Fixes over time */}
                <Card>
                  <CardHeader>
                    <CardTitle>Features vs Bug Fixes</CardTitle>
                    <CardDescription>Daily commit breakdown — features shipped vs bugs fixed vs deploys</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="Features" fill="#22c55e" />
                        <Bar dataKey="Bug Fixes" fill="#f97316" />
                        <Bar dataKey="Deploys" fill="#8b5cf6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Studio activity */}
                <Card>
                  <CardHeader>
                    <CardTitle>Studio Activity</CardTitle>
                    <CardDescription>Commits per studio per day</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={studioData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="Demand" fill="#3b82f6" stackId="a" />
                        <Bar dataKey="Offering" fill="#10b981" stackId="a" />
                        <Bar dataKey="Answer" fill="#f59e0b" stackId="a" />
                        <Bar dataKey="Conversation" fill="#8b5cf6" stackId="a" />
                        <Bar dataKey="Impact" fill="#ec4899" stackId="a" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* AI cost over time */}
                <Card>
                  <CardHeader>
                    <CardTitle>Claude Code Cost & Tokens</CardTitle>
                    <CardDescription>Daily estimated cost (USD) and tokens processed (thousands)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={260}>
                      <ComposedChart data={costData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="cost" orientation="left" tickFormatter={(v) => `$${v}`} />
                        <YAxis yAxisId="tokens" orientation="right" tickFormatter={(v) => `${v}K`} />
                        <Tooltip />
                        <Legend />
                        <Bar yAxisId="cost" dataKey="Cost (USD)" fill="#ef4444" />
                        <Line yAxisId="tokens" type="monotone" dataKey="Tokens (K)" stroke="#6366f1" dot={false} strokeWidth={2} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </>
            );
          })()}
        </TabsContent>
      </Tabs>
    </div>
  );
}
